import type { HTMLProps, ReactNode } from "react";
import { forwardRef } from "react";

import classNames from "classnames";

import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { Button } from "@jti/core-components/components/Button";
import type { SectionState } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./CollapsibleSection.module.scss";

type CollapsibleSectionProps = {
  state: SectionState;
  title: ReactNode;
  children: ReactNode;
  isEditButtonDisabled?: boolean;
  onEdit?: () => void;
} & HTMLProps<HTMLDivElement>;

export const CollapsibleSection = forwardRef<HTMLDivElement, CollapsibleSectionProps>(
  ({ state, title, onEdit, children, className, isEditButtonDisabled, ...restProps }, ref) => {
    const { __ } = useTranslations();
    const isCompleted = state === "completed";
    const statesWithVisibleContent: SectionState[] = ["active", "editing", "completed"];
    const shouldShowContent = statesWithVisibleContent.includes(state);
    const isEditButtonVisible = isCompleted && !isEditButtonDisabled && typeof onEdit === "function";

    return (
      <div
        ref={ref}
        className={classNames([styles.collapsible, styles[state], className])}
        data-testid="collapsible"
        {...restProps}
      >
        <div className={styles.header}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.title}>{title}</div>
            {isCompleted && <UiCheckCircleIcon className={styles.checkIcon} />}
          </div>
          {isEditButtonVisible && (
            <Button data-testid="collapsible-edit-button" variant="text" size="lg" onClick={onEdit}>
              {__("collapsible.component.edit")}
            </Button>
          )}
        </div>

        {shouldShowContent && children}
      </div>
    );
  }
);

CollapsibleSection.displayName = "CollapsibleSection";
